import React, { useContext, useEffect, useRef, useState } from 'react';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Calendar } from 'primereact/calendar';
import dayjs, { ManipulateType, UnitTypeLong } from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  dateFromDayjsInTz,
  dayjsFromDateTimeInTz,
} from '@utils/dateWithTz.tsx';
import { stampDataTestIdOnDescendantRef } from '@utils/testUtils';

interface QuickPrefill {
  value?: number;
  unit?: UnitTypeLong;
  label?: string;
  staticValue?: dayjs.Dayjs | dayjs.Dayjs[];
}

interface ComponentProps {
  value: dayjs.Dayjs;
  onChange: (localDate: dayjs.Dayjs | dayjs.Dayjs[], timeZone?: string) => void;
  timeZone: string;
  fullWidth?: boolean;
  quickPrefills?: QuickPrefill[];
  code?: string;
  showTime?: boolean;
  readOnly?: boolean;
  dataTestIdCalendar?: string;
  dataTestIdTimezone?: string;
}


const InputDateAndTime: React.FC<ComponentProps> = ({
                                                      value,
                                                      onChange,
                                                      timeZone: initialTimeZone,
                                                      fullWidth = false,
                                                      quickPrefills,
                                                      code,
                                                      showTime = true,
                                                      readOnly = false,
                                                      dataTestIdCalendar,
                                                      dataTestIdTimezone,
                                                    }) => {
  const { getAvailableTimezones } = useContext(ApiContext);
  const [timezones, setTimezones] = useState<{name: string, code: string}[]>([]);
  const [timeZone, setTimeZone] = useState<string | undefined>(initialTimeZone);
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null);
  const timezoneWrapperRef = useRef<HTMLDivElement | null>(null);

  // Sync internal timeZone state with prop changes
  useEffect(() => {
    setTimeZone(initialTimeZone);
  }, [initialTimeZone]);


  useEffect(() => {
    getAvailableTimezones(
      (data) => {
        // Filter out abbreviated timezones (like EST, PST) and legacy/non-standard ones (like SystemV/, Etc/, US/, Canada/, GMT/)
        // Keep only proper IANA timezone regions: Africa, America, Antarctica, Arctic, Asia, Atlantic, Australia, Europe, Indian, Pacific
        const validRegions = ['Africa/', 'America/', 'Antarctica/', 'Arctic/', 'Asia/', 'Atlantic/', 'Australia/', 'Europe/', 'Indian/', 'Pacific/', 'UTC'];
        const filteredTimezones = data.filter(tz =>
          validRegions.some(region => tz.startsWith(region))
        );
        const timezoneOptions = filteredTimezones.map(tz => ({ name: tz, code: tz }));
        setTimezones(timezoneOptions);
      },
      code
    );
  }, [code, getAvailableTimezones]);

  const calendarValue = timeZone && value ? (dateFromDayjsInTz(value, timeZone) as Date) : undefined;

  useEffect(() => {
    stampDataTestIdOnDescendantRef(calendarWrapperRef, '.p-inputtext', dataTestIdCalendar);
    stampDataTestIdOnDescendantRef(timezoneWrapperRef, '.p-dropdown', dataTestIdTimezone);
  }, [dataTestIdCalendar, dataTestIdTimezone, calendarValue, timeZone, timezones.length]);
  console.log('Rendering InputDateAndTime with value:', value, 'and timeZone:', timeZone, 'Calendar value:', calendarValue);

  return (
    <div className={fullWidth ? 'w-full flex-grow' : ''}>
      <div className={fullWidth ? 'grid grid-cols-2 min-h-[40px] gap-2 w-full' : 'grid grid-cols-2 min-h-[40px] gap-2'}>
        <div
          ref={calendarWrapperRef}
          className="w-full [&_.p-calendar]:h-[42px] [&_.p-inputtext]:h-[42px] [&_.p-datepicker-trigger]:h-[42px]"
        >
          <Calendar
            value={calendarValue || dayjs().toDate()}
            onChange={e => {
              console.log('Date changed to:', e?.value);
              if (e?.value instanceof Date) {
                const next = dayjsFromDateTimeInTz(e.value, timeZone);
                onChange(next as dayjs.Dayjs, timeZone);
              }
            }}
            showTime={showTime}
            showIcon
            locale={i18n.language}
            selectionMode={'single'}
            disabled={readOnly}
            style={{ width: '100%' }}
            inputStyle={{ width: '100%' }}
          />
        </div>
        <div
          ref={timezoneWrapperRef}
          className="w-full"
        >
          <Dropdown
            value={timeZone}
            options={timezones}
            filter
            onChange={e => {
              const newTimeZone = e.value;
              console.log('Timezone changed to:', newTimeZone);
              setTimeZone(newTimeZone);
              onChange(value, newTimeZone);
            }}
            optionLabel="name"
            optionValue="code"
            placeholder={tr(`InputDateAndTime.selectATimezone`, `Select a timezone`)}
            filterBy="name"
            disabled={readOnly}
            style={{ width: '100%' }}
          />
        </div>
      </div>
      {!readOnly && quickPrefills && quickPrefills.length > 0 && (
        <div className="flex gap-2 mb-2 items-center justify-start">
          {quickPrefills.map((prefill, idx) => (
            <React.Fragment key={idx}>
              <div
                onClick={() => {
                  console.log('Quick prefill selected:', prefill);
                  if (prefill.staticValue) {
                    onChange(prefill.staticValue, timeZone);
                    return;
                  } else {
                    onChange(dayjs().add(prefill.value, prefill.unit as ManipulateType), timeZone);
                  }
                }}
                className='text-content-brand cursor-pointer'
              >
                {prefill.label}
              </div>
              {idx < quickPrefills.length - 1 && (
                <span className="mx-1 text-gray-400">|</span>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default InputDateAndTime;
